import { make } from 'wonka';
import { logError } from '../logger';
import { safeWhile } from '../utils/util';

type GlobalWindow = Window & typeof globalThis;

interface TCFReturn {
  __tcfapiReturn?: {
    returnValue: string;
    success: boolean;
    callId: number | string;
  };
}

type CMPCallbacks = Record<string, { cb: Function; tid: number }>;

/**
 * The TCF API version we're using
 */
const TCF_VERSION = 2;

/**
 * When we make calls to the TCF API, if we're in an iframe and we don't find our magic iframe then we postMessage()
 * to the top window, but if there's no TCF there then nobody will respond to our calls. If the API is there it should
 * return immediately, so the timeout can be short, but I'm using a longer value _in case_ the framework is somehow
 * delayed in being added to the page (again, this should never happen on a real page).
 */
const TCF_CALL_TIMEOUT = 500;

/**
 * Get a reference to the TCF API which you call as normal. Typically we return window.__tcfapi() but if we're in an iframe etc then
 * instead we walk the list of frames to find the "magic" one, we then setup a postMessage() proxy that will manage routing responses
 * to their correct callback.
 * @see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#cmp-api-v20
 */
function getTcfApi() {
  //  NOTE - we wrap this in a function rather than just defining it as a global const because
  //  we bind event handlers, and we only want to do things like that if specifically called.
  const tcfApi =
    window.__tcfapi ||
    (() => {
      //  MODIFIED VERSION of the IAB's stub
      let frame = window;
      let cmpFrame: GlobalWindow | null = null;
      const cmpCallbacks: CMPCallbacks = {};

      safeWhile(
        () => !!frame,
        (breakFn) => {
          try {
            if (frame.frames['__tcfapiLocator']) {
              cmpFrame = frame;
              breakFn();
              return;
            }
          } catch (ignore) {}
          if (frame === window.top) {
            breakFn();
            return;
          }
          frame = frame.parent as GlobalWindow;
        },
        (elapsed) => {
          logError({
            error: `TCF API not found in duration: ${elapsed}`,
            detail: elapsed,
            impressionId: '',
            tagId: '0',
            reason: 'lib-err-duration',
          });
        },
      );

      const proxy: Window['__tcfapi'] = function (cmd, version, callback, arg) {
        if (!cmpFrame) {
          callback({ msg: 'CMP not found' }, false);
        } else {
          const callId = Math.random() + '';
          const msg = {
            __tcfapiCall: {
              command: cmd,
              parameter: arg,
              version: version,
              callId: callId,
            },
          };

          cmpCallbacks[callId] = {
            cb: callback,
            tid: window.setTimeout(() => {
              delete cmpCallbacks[callId];
              callback({ msg: 'CMP timeout' }, false);
            }, TCF_CALL_TIMEOUT),
          };
          cmpFrame.postMessage(msg, '*');
        }
      };

      function postMessageHandler(event: { data?: string }) {
        let json: TCFReturn = {};

        try {
          json =
            typeof event.data === 'string'
              ? JSON.parse(event.data)
              : event.data;
        } catch (ignore) {}

        const payload = json.__tcfapiReturn;
        if (payload) {
          let o = cmpCallbacks[payload.callId];
          if (o) {
            delete cmpCallbacks[payload.callId];
            window.clearTimeout(o.tid);
            o.cb(payload.returnValue, payload.success);
          }
        }
      }
      window.addEventListener('message', postMessageHandler, false);

      return proxy;
    })();

  return tcfApi;
}

/**
 * Create an observable source for TCF data. This will emit the current TCF data over time
 * which can be consumed elsewhere in the application.
 * If there is no GDPR consent then the observable will complete immediately.
 * Note: TCF's "addEventListener" is initially synchronous, so we should get a value immediately
 * but it will emit events over time as the user interacts with publisher consent settings.
 */
export const tcfData$ = () =>
  make<{ gdprApplies: Boolean; tcString: string }>(({ next, complete }) => {
    const tcfApi = getTcfApi();
    // We get a listenerId back from the TCF API which we need to use to remove the listener
    // which we need when we unsubscribe from the observable
    let listenerId: string | number | undefined;
    tcfApi(
      'addEventListener',
      TCF_VERSION,
      (
        data: {
          gdprApplies: boolean;
          tcString: string;
          listenerId: string | number;
        },
        success: boolean,
      ) => {
        listenerId = data?.listenerId;

        if (success) {
          next(data);
        } else {
          next({ gdprApplies: false, tcString: '' });
          complete();
        }
      },
    );

    // Cleanup on unsubscription
    return () => {
      if (listenerId) {
        tcfApi('removeEventListener', TCF_VERSION, () => {}, listenerId);
      }
    };
  });
